import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import type {
	FreeArea,
	HostToClientMessage,
	MessagePayload,
	OrientationType,
} from "@rubyplay/effw-connector";
import type { EngagementProviderBootInput } from "@/provider/ProviderTypes";
import { getFreeAreas } from "@/config/freeAreasConfig";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import {
	buildHostProviderBootInput,
	isHostConfigured,
	isHostGameReady,
	isProviderBootEligible,
	type HostFreeArea,
	type HostOrientation,
	type HostPlatform,
	type HostSessionConfig,
	type HostSessionSnapshot,
} from "@/host/HostSessionTypes";
import { getHostSessionStore } from "@/host/HostSessionStore";
import { debugLog } from "@/utils/debug";
import { useSubscription } from "@/hooks/useSubscription";

export type HostSessionState = {
	hostTransportReady: boolean;
	hostConfigured: boolean;
	hostGameReady: boolean;
	providerBootEligible: boolean;
	providerBootInput: EngagementProviderBootInput | null;
};

function normalizeIntegrationType(rawIntegrationType: unknown): string | undefined {
	if (typeof rawIntegrationType !== "string") {
		return undefined;
	}

	const normalized = rawIntegrationType.trim().toLowerCase();

	if (!normalized) {
		return undefined;
	}

	return normalized;
}

function toHostPlatform(rawPlatform: unknown): HostPlatform {
	return rawPlatform === "mobile" ? "mobile" : "desktop";
}

function toHostOrientation(rawOrientation: unknown): HostOrientation {
	return rawOrientation === "portrait" ? "portrait" : "landscape";
}

function toHostSessionConfig(
	payload: MessagePayload<HostToClientMessage, "ConfigConnection">
): HostSessionConfig {
	return {
		sessionKey: String(payload.sessionKey ?? "").trim(),
		serverUrl: String(payload.serverUrl ?? "").trim(),
		gameName: String(payload.gameName ?? "").trim(),
		lang: String(payload.lang ?? "").trim(),
		currency: String(payload.currency ?? "").trim(),
		operator: String(payload.operator ?? "").trim(),
		platform: toHostPlatform(payload.platform),
		orientation: toHostOrientation(payload.orientation),
		integrationType: normalizeIntegrationType(payload.integrationType),
		isHomeButtonEnabled: Boolean(payload.isHomeButtonEnabled),

		bet: typeof payload.bet === "number" ? payload.bet : undefined,
		subunit:
			typeof payload.subunit === "number" ? payload.subunit : undefined,
		funMode:
			typeof payload.funMode === "boolean" ? payload.funMode : undefined,
		funModeBalance:
			typeof payload.funModeBalance === "number"
				? payload.funModeBalance
				: undefined,
		regulationType:
			typeof payload.regulationType === "string"
				? payload.regulationType
				: undefined,
		iframeContainerId:
			typeof payload.iframeContainerId === "string"
				? payload.iframeContainerId
				: undefined,
	};
}

function toHostFreeAreas(freeAreas: FreeArea[]): HostFreeArea[] {
	return freeAreas.map((area) => ({
		version: String(area.version ?? ""),
		id: String(area.id ?? ""),
		gameName: String(area.gameName ?? ""),
		x: Number(area.x ?? 0),
		y: Number(area.y ?? 0),
		width: Number(area.width ?? 0),
		height: Number(area.height ?? 0),
		platform: area.platform === "mobile" ? "mobile" : "desktop",
		orientation:
			area.orientation === "portrait" ? "portrait" : "landscape",
		tooltipPosition: area.tooltipPosition,
	}));
}

function toEngagementProviderBootInput(
	snapshot: HostSessionSnapshot
): EngagementProviderBootInput | null {
	const hostBootInput = buildHostProviderBootInput(snapshot);

	if (!hostBootInput) {
		return null;
	}

	return {
		sessionKey: hostBootInput.sessionKey,
		serverUrl: hostBootInput.serverUrl,
		gameName: hostBootInput.gameName,
		lang: hostBootInput.lang,
		operator: hostBootInput.operator,
		platform: hostBootInput.platform,
		orientation: hostBootInput.orientation,
		integrationType: hostBootInput.integrationType,
	};
}

export function useHostSession(): HostSessionState {
	const {
		onHostError,
		onConfigConnection,
		onGameReady,
		ackConfigConnection,
		onOrientationChange,
		onUpdateFreeAreas,
		isStandalone,
	} = useCommunicator();

	const standalone = isStandalone();
	const hostSessionStore = getHostSessionStore();

	const snapshot = useSyncExternalStore(
		(onStoreChange) => hostSessionStore.subscribe(() => onStoreChange()),
		() => hostSessionStore.getSnapshot(),
		() => hostSessionStore.getSnapshot()
	);

	const configAckSentRef = useRef(false);

	useEffect(() => {
		if (standalone) {
			hostSessionStore.dispatch({ type: "TRANSPORT_READY" });
		}
	}, [hostSessionStore, standalone]);

	useSubscription(
		onGameReady,
		(_payload: MessagePayload<HostToClientMessage, "GameReady">) => {
			debugLog("[EF HostSession] host -> GameReady", {
				phaseBefore: snapshot.phase,
				transportReadyBefore: snapshot.transportReady,
				configuredBefore: snapshot.configured,
				gameReadyBefore: snapshot.gameReady,
			});

			hostSessionStore.dispatch({
				type: "GAME_READY_RECEIVED",
			});
		},
		[
			hostSessionStore,
			snapshot.configured,
			snapshot.gameReady,
			snapshot.phase,
			snapshot.transportReady,
		]
	);

	useSubscription(
		onHostError,
		(_payload: MessagePayload<HostToClientMessage, "Error">) => {
			// debugError("[EF HostSession] host -> Error", payload);
		},
		[]
	);

	useSubscription(
		onConfigConnection,
		(payload: MessagePayload<HostToClientMessage, "ConfigConnection">) => {
			console.log("[EF HostSession] Received ConfigConnection", payload);
			const initialAreas: FreeArea[] = getFreeAreas(payload.gameName, payload.isHomeButtonEnabled);
			const nextConfig = toHostSessionConfig(payload);
			const nextHostFreeAreas = toHostFreeAreas(initialAreas);

			debugLog("[EF HostSession] host -> ConfigConnection", {
				gameName: payload.gameName,
				platform: payload.platform,
				orientation: payload.orientation,
				operator: payload.operator,
				integrationType: payload.integrationType,
				isHomeButtonEnabled: payload.isHomeButtonEnabled,
				sessionKeyPresent: !!payload.sessionKey,
				phaseBefore: snapshot.phase,
				transportReadyBefore: snapshot.transportReady,
				configuredBefore: snapshot.configured,
				gameReadyBefore: snapshot.gameReady,
			});

			hostSessionStore.dispatch({
				type: "CONFIG_RECEIVED",
				payload: {
					config: nextConfig,
					freeAreas: nextHostFreeAreas,
				},
			});

			configAckSentRef.current = false;

			if (!configAckSentRef.current) {
				configAckSentRef.current = true;

				ackConfigConnection({
					success: true,
					freeAreas: initialAreas,
					assetsToLoadCount: 0,
					isMultiFreeZone: true,
				});

				debugLog("[EF HostSession] ConfigConnectionAck sent", {
					success: true,
					platform: nextConfig.platform,
					orientation: nextConfig.orientation,
					freeAreasCount: initialAreas.length,
					phaseAfter: hostSessionStore.getSnapshot().phase,
					transportReadyAfter: hostSessionStore.getSnapshot().transportReady,
					configuredAfter: hostSessionStore.getSnapshot().configured,
				});
			}
		},
		[
			ackConfigConnection,
			hostSessionStore,
			snapshot.configured,
			snapshot.gameReady,
			snapshot.phase,
			snapshot.transportReady,
		]
	);

	useSubscription(
		onOrientationChange,
		(payload: { orientation: OrientationType }) => {
			debugLog("[EF HostSession] host -> Orientation", payload);

			hostSessionStore.dispatch({
				type: "ORIENTATION_UPDATED",
				payload: {
					orientation:
						payload.orientation === "portrait"
							? "portrait"
							: "landscape",
				},
			});
		},
		[hostSessionStore]
	);

	useSubscription(
		onUpdateFreeAreas,
		(payload: MessagePayload<HostToClientMessage, "UpdateFreeAreas">) => {
			const next: FreeArea[] = Array.isArray(payload.freeAreas)
				? (payload.freeAreas as FreeArea[])
				: [];

			debugLog("[EF HostSession] host -> UpdateFreeAreas", {
				count: next.length,
				areaIds: next.map((area: FreeArea) => area.id),
			});

			hostSessionStore.dispatch({
				type: "FREE_AREAS_UPDATED",
				payload: {
					freeAreas: toHostFreeAreas(next),
				},
			});
		},
		[hostSessionStore]
	);

	const derivedState = useMemo<HostSessionState>(() => {
		const hostTransportReady = standalone || snapshot.transportReady;
		const hostConfigured = standalone || isHostConfigured(snapshot);
		const hostGameReady = standalone || isHostGameReady(snapshot);
		const providerBootEligible = isProviderBootEligible(snapshot);
		const providerBootInput = toEngagementProviderBootInput(snapshot);

		return {
			hostTransportReady,
			hostConfigured,
			hostGameReady,
			providerBootEligible,
			providerBootInput,
		};
	}, [snapshot, standalone]);

	useEffect(() => {
		debugLog("[EF HostSession] derived state snapshot", {
			standalone,
			phase: snapshot.phase,
			hostTransportReady: derivedState.hostTransportReady,
			hostConfigured: derivedState.hostConfigured,
			hostGameReady: derivedState.hostGameReady,
			providerBootEligible: derivedState.providerBootEligible,
			hasProviderBootInput: !!derivedState.providerBootInput,
			lastUpdatedAtMs: snapshot.lastUpdatedAtMs,
		});
	}, [derivedState, snapshot, standalone]);

	return derivedState;
}